import { discountPercent, STORE_NAME, type SareeDTO } from '@neyara/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError } from '../lib/api';
import { useCart } from '../lib/cart';
import { useSaree, useSarees } from '../lib/queries';
import { usePinOnDesktop } from '../lib/scroll';
import { useWhatsApp } from '../lib/whatsapp';
import { BagIcon, ChevronIcon, MinusIcon, PlusIcon, WhatsAppIcon } from '../components/icons';
import { Lotus, LotusDivider } from '../components/lotus';
import { Price } from '../components/price';
import { SareeCard } from '../components/saree-card';
import { SareeImage } from '../components/saree-image';
import { Seal } from '../components/seal';
import { NotFoundPage } from './not-found';

export function SareePage() {
  const { slug } = useParams();
  const { data: saree, isPending, error } = useSaree(slug);

  if (error instanceof ApiError && error.status === 404) return <NotFoundPage />;
  if (isPending) return <DetailSkeleton />;
  if (!saree) {
    return (
      <div className="tissue grid min-h-[60svh] place-items-center px-6 text-center">
        <p className="font-script text-3xl text-maroon italic">We couldn't load this saree. Please refresh.</p>
      </div>
    );
  }
  return <SareeDetail key={saree.id} saree={saree} />;
}

function SareeDetail({ saree }: { saree: SareeDTO }) {
  const soldOut = saree.stock <= 0;
  const off = discountPercent(saree.price, saree.compareAtPrice);
  const layout = useRef<HTMLDivElement>(null);
  const gallery = useRef<HTMLDivElement>(null);
  usePinOnDesktop(gallery, layout, [saree.id]);

  return (
    <>
      <title>{`${saree.name} - ${STORE_NAME}`}</title>
      <meta name="description" content={saree.description.slice(0, 155) || `${saree.name} from ${STORE_NAME}.`} />

      <div className="tissue">
        <div className="mx-auto max-w-7xl px-4 pt-6 pb-20 sm:px-8 sm:pt-8">
          <nav aria-label="Breadcrumb" className="text-sm text-ink-soft">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/sarees" className="hover:text-maroon">
                  Sarees
                </Link>
              </li>
              {saree.category && (
                <>
                  <ChevronIcon size={14} aria-hidden="true" />
                  <li>
                    <Link to={`/sarees?category=${saree.category.slug}`} className="hover:text-maroon">
                      {saree.category.name}
                    </Link>
                  </li>
                </>
              )}
            </ol>
          </nav>

          <div ref={layout} className="mt-6 grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:items-start lg:gap-16">
            <div ref={gallery}>
              <Gallery saree={saree} />
            </div>

            <div className="lg:pt-4">
              <h1 className="font-display text-[1.65rem] leading-tight font-medium tracking-[0.1em] text-charcoal uppercase sm:text-[2.1rem]">
                {saree.name}
              </h1>
              {(saree.category || saree.fabric) && (
                <p className="mt-2 font-script text-2xl leading-tight text-ink-soft italic">
                  {[saree.category?.name, saree.fabric].filter(Boolean).join(' · ')}
                </p>
              )}

              <div className="mt-5 flex items-center gap-4">
                <Price price={saree.price} compareAtPrice={saree.compareAtPrice} className="text-2xl text-maroon" />
                {off !== null && !soldOut && (
                  <Seal tone="pink" size="sm" className="-rotate-10">
                    {off}% off
                  </Seal>
                )}
              </div>
              <StockLine stock={saree.stock} />

              <LotusDivider className="my-7" align="start" />

              {soldOut ? <SoldOutActions saree={saree} /> : <BuyActions saree={saree} />}

              <TagPanel saree={saree} />

              {saree.description && (
                <section className="mt-10" aria-labelledby="about-title">
                  <h2 id="about-title" className="font-display text-sm tracking-[0.22em] text-maroon uppercase">
                    About this saree
                  </h2>
                  <p className="mt-3 max-w-prose whitespace-pre-line text-charcoal/90">{saree.description}</p>
                </section>
              )}
              {saree.careInstructions && (
                <section className="mt-8" aria-labelledby="care-title">
                  <h2 id="care-title" className="font-display text-sm tracking-[0.22em] text-maroon uppercase">
                    Care
                  </h2>
                  <p className="mt-3 max-w-prose whitespace-pre-line text-charcoal/90">{saree.careInstructions}</p>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>

      <MoreLikeThis saree={saree} />
    </>
  );
}

function Gallery({ saree }: { saree: SareeDTO }) {
  const [index, setIndex] = useState(0);
  const images = saree.images;
  const current = images[index];

  return (
    <div>
      <div className="relative aspect-[3/4] overflow-hidden bg-champagne">
        <SareeImage
          key={current?.key ?? 'none'}
          image={current}
          name={saree.name}
          eager
          className={`size-full animate-[fade-in_.4s_ease-out] ${saree.stock <= 0 ? 'saturate-[.6]' : ''}`}
        />
        {saree.stock <= 0 && <Seal tone="charcoal" className="absolute top-4 right-4 -rotate-10">Sold out</Seal>}
      </div>
      {images.length > 1 && (
        <ul className="mt-3 flex gap-2 overflow-x-auto pb-1" aria-label="Photos">
          {images.map((img, i) => (
            <li key={img.key} className="shrink-0">
              <button
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Show photo ${i + 1} of ${images.length}`}
                aria-current={i === index}
                className={`block h-24 w-[4.5rem] overflow-hidden ring-offset-2 ring-offset-tissue transition-shadow sm:h-28 sm:w-[5.25rem] ${
                  i === index ? 'ring-2 ring-maroon' : 'opacity-75 hover:opacity-100'
                }`}
              >
                <img src={img.url} alt="" className="size-full object-cover" loading="lazy" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StockLine({ stock }: { stock: number }) {
  if (stock <= 0) return <p className="mt-2 text-ink-soft">This saree has found its home.</p>;
  if (stock <= 2)
    return (
      <p className="mt-2 font-medium text-maroon-soft">
        Only {stock} left
      </p>
    );
  return <p className="mt-2 text-olive">In stock</p>;
}

function BuyActions({ saree }: { saree: SareeDTO }) {
  const { add, open, items } = useCart();
  const whatsapp = useWhatsApp();
  const [quantity, setQuantity] = useState(1);
  const [stamped, setStamped] = useState(0);
  const inBag = items.find((i) => i.id === saree.id)?.quantity ?? 0;
  const roomLeft = saree.stock - inBag;

  useEffect(() => {
    if (!stamped) return;
    const t = setTimeout(() => setStamped(0), 2600);
    return () => clearTimeout(t);
  }, [stamped]);

  const addToBag = () => {
    add(saree, quantity);
    setStamped((n) => n + 1);
  };

  const orderHref = whatsapp?.order([{ name: saree.name, slug: saree.slug, price: saree.price, quantity }]);

  return (
    <div>
      {saree.stock > 1 && (
        <div className="mb-5 flex items-center gap-4">
          <span className="label mb-0" id="qty-label">
            Quantity
          </span>
          <div className="flex items-center border border-maroon/40" role="group" aria-labelledby="qty-label">
            <button
              type="button"
              className="grid size-11 place-items-center text-maroon disabled:opacity-35"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
              aria-label="One fewer"
            >
              <MinusIcon size={18} />
            </button>
            <span className="tabular w-10 text-center" aria-live="polite">
              {quantity}
            </span>
            <button
              type="button"
              className="grid size-11 place-items-center text-maroon disabled:opacity-35"
              onClick={() => setQuantity((q) => Math.min(saree.stock, q + 1))}
              disabled={quantity >= saree.stock}
              aria-label="One more"
            >
              <PlusIcon size={18} />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        {orderHref ? (
          <a href={orderHref} target="_blank" rel="noopener noreferrer" className="btn btn-maroon flex-1">
            <WhatsAppIcon size={20} /> Order on WhatsApp
          </a>
        ) : (
          <button type="button" disabled className="btn btn-maroon flex-1">
            Ordering opens soon
          </button>
        )}
        <div className="relative flex-1">
          <button
            type="button"
            onClick={inBag && roomLeft <= 0 ? open : addToBag}
            className="btn btn-outline w-full"
          >
            <BagIcon size={19} />
            {inBag && roomLeft <= 0 ? 'View your bag' : 'Add to bag'}
          </button>
          {stamped > 0 && (
            <span
              key={stamped}
              role="status"
              className="pointer-events-none absolute -top-7 -right-3 animate-[seal-press_.55s_var(--ease-out-expo)_both]"
            >
              <Seal tone="maroon" size="sm">
                Added
              </Seal>
            </span>
          )}
        </div>
      </div>
      {inBag > 0 && (
        <p className="mt-3 text-sm text-ink-soft">
          {inBag} in your bag ·{' '}
          <button type="button" onClick={open} className="text-maroon underline">
            View bag
          </button>
        </p>
      )}
    </div>
  );
}

function SoldOutActions({ saree }: { saree: SareeDTO }) {
  const whatsapp = useWhatsApp();
  return (
    <div>
      {whatsapp ? (
        <a
          href={whatsapp.enquire({ name: saree.name, slug: saree.slug })}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-maroon w-full sm:w-auto"
        >
          <WhatsAppIcon size={20} /> Ask about a similar saree
        </a>
      ) : (
        <Link to="/sarees" className="btn btn-outline">
          Browse other sarees
        </Link>
      )}
    </div>
  );
}

/** Saree facts on a large swing tag. */
function TagPanel({ saree }: { saree: SareeDTO }) {
  const rows: [string, ReactNode][] = [];
  if (saree.category) rows.push(['Weave', saree.category.name]);
  if (saree.fabric) rows.push(['Fabric', saree.fabric]);
  if (saree.color) rows.push(['Colour', saree.color]);
  if (saree.occasions.length) rows.push(['Occasion', saree.occasions.join(', ')]);
  rows.push(['Blouse piece', saree.blouseIncluded ? 'Included' : 'Not included']);
  if (saree.lengthMeters) rows.push(['Length', `${saree.lengthMeters} m`]);

  return (
    <div data-reveal className="tag-drop mt-10">
      <div className="tag-shape bg-champagne px-6 pt-9 pb-6">
        <span
          aria-hidden="true"
          className="absolute top-3.5 left-1/2 size-3 -translate-x-1/2 rounded-full bg-tissue ring-1 ring-champagne-deep ring-inset"
        />
        <h2 className="sr-only">Details</h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5">
          {rows.map(([term, value]) => (
            <div key={term} className="contents">
              <dt className="font-display text-[0.72rem] leading-6 tracking-[0.18em] text-ink-soft uppercase">{term}</dt>
              <dd className="text-charcoal">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function MoreLikeThis({ saree }: { saree: SareeDTO }) {
  const { data } = useSarees({ category: saree.category?.slug, pageSize: 5 });
  const others = (data?.items ?? []).filter((s) => s.id !== saree.id).slice(0, 4);
  if (others.length === 0) return null;

  return (
    <section aria-labelledby="more-title" className="bg-champagne py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div data-reveal className="flex flex-col items-center text-center">
          <h2 id="more-title" className="font-display text-[1.5rem] font-medium tracking-[0.18em] text-maroon uppercase sm:text-[1.9rem]">
            {saree.category ? `More ${saree.category.name}` : 'You may also like'}
          </h2>
          <LotusDivider className="mt-4" />
        </div>
        <div className="mt-12 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-8 lg:grid-cols-4">
          {others.map((s) => (
            <SareeCard key={s.id} saree={s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function DetailSkeleton() {
  return (
    <div className="tissue" aria-busy="true" aria-label="Loading saree">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-8 lg:grid-cols-2 lg:gap-16">
        <div className="aspect-[3/4] animate-pulse bg-champagne/70" />
        <div className="space-y-4 pt-4">
          <div className="h-6 w-32 animate-pulse bg-champagne/70" />
          <div className="h-10 w-4/5 animate-pulse bg-champagne/70" />
          <div className="h-8 w-40 animate-pulse bg-champagne/70" />
          <Lotus className="mt-10 h-8 w-12 text-champagne-deep" />
        </div>
      </div>
    </div>
  );
}
